import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment";
import {
  COLOR_BY_INTENSITY,
  COLOR_HOVER,
  COLOR_SELECTED,
  Intensity,
  ZoneId,
} from "./zones";

const MODEL_URL = "/body.glb";
const DRACO_DECODER_URL = "https://www.gstatic.com/draco/versioned/decoders/1.5.6/";
const ZONE_PREFIX = "zone_";
const IGNORED_ZONES = new Set(["altro"]);

interface UseBodyViewerOptions {
  intensities: Partial<Record<ZoneId, Intensity>>;
  onSelectZone: (zoneId: ZoneId | null) => void;
}

interface UseBodyViewerResult {
  containerRef: React.RefObject<HTMLDivElement>;
  meshCount: number | null;
  loadError: string | null;
  hoveredZone: ZoneId | null;
  selectedZone: ZoneId | null;
  zoomToZone: (zoneId: ZoneId) => void;
  resetCamera: () => void;
}

const DEFAULT_CAM_POS = new THREE.Vector3(0, 0.2, 3.2);
const DEFAULT_CAM_TARGET = new THREE.Vector3(0, 0, 0);

export function useBodyViewer(options: UseBodyViewerOptions): UseBodyViewerResult {
  const { intensities, onSelectZone } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [meshCount, setMeshCount] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hoveredZone, setHoveredZone] = useState<ZoneId | null>(null);
  const [selectedZone, setSelectedZone] = useState<ZoneId | null>(null);

  const stateRef = useRef({
    meshes: new Map<ZoneId, THREE.Mesh[]>(),
    camera: null as THREE.PerspectiveCamera | null,
    controls: null as InstanceType<typeof OrbitControls> | null,
    targetPos: DEFAULT_CAM_POS.clone(),
    targetLook: DEFAULT_CAM_TARGET.clone(),
    hoveredZone: null as ZoneId | null,
    selectedZone: null as ZoneId | null,
    intensities: {} as Partial<Record<ZoneId, Intensity>>,
    onSelectZone,
  });

  stateRef.current.onSelectZone = onSelectZone;
  stateRef.current.intensities = intensities;

  const imperativeApi = useMemo(
    () => ({
      zoomToZone: (zoneId: ZoneId) => zoomToZoneImpl(stateRef.current, zoneId),
      resetCamera: () => resetCameraImpl(stateRef.current),
    }),
    []
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const cleanup = mountViewer(container, stateRef, {
      setMeshCount,
      setLoadError,
      setHoveredZone,
      setSelectedZone,
    });
    return cleanup;
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    for (const [zoneId, meshes] of s.meshes) {
      const intensity = (intensities[zoneId] ?? 0) as Intensity;
      for (const mesh of meshes) {
        applyColor(mesh, zoneId, intensity, s.hoveredZone, s.selectedZone);
      }
    }
  }, [intensities, hoveredZone, selectedZone]);

  return {
    containerRef,
    meshCount,
    loadError,
    hoveredZone,
    selectedZone,
    ...imperativeApi,
  };
}

// ─────────────────────────────────────────────────────────────

type ViewerState = {
  meshes: Map<ZoneId, THREE.Mesh[]>;
  camera: THREE.PerspectiveCamera | null;
  controls: InstanceType<typeof OrbitControls> | null;
  targetPos: THREE.Vector3;
  targetLook: THREE.Vector3;
  hoveredZone: ZoneId | null;
  selectedZone: ZoneId | null;
  intensities: Partial<Record<ZoneId, Intensity>>;
  onSelectZone: (zoneId: ZoneId | null) => void;
};

interface MountCallbacks {
  setMeshCount: (n: number) => void;
  setLoadError: (msg: string) => void;
  setHoveredZone: (z: ZoneId | null) => void;
  setSelectedZone: (z: ZoneId | null) => void;
}

function mountViewer(
  container: HTMLDivElement,
  stateRef: { current: ViewerState },
  cb: MountCallbacks
): () => void {
  const width = container.clientWidth;
  const height = container.clientHeight;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#1a1a22");

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
  camera.position.copy(DEFAULT_CAM_POS);
  stateRef.current.camera = camera;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
  keyLight.position.set(5, 8, 5);
  scene.add(keyLight);
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
  fillLight.position.set(-5, 3, -5);
  scene.add(fillLight);
  scene.add(new THREE.AmbientLight(0xffffff, 0.4));

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;
  controls.enablePan = false;
  controls.minDistance = 0.3;
  controls.maxDistance = 6;
  controls.target.copy(DEFAULT_CAM_TARGET);
  stateRef.current.controls = controls;

  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(DRACO_DECODER_URL);
  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(dracoLoader);

  let disposed = false;

  gltfLoader.load(
    MODEL_URL,
    (gltf: { scene: THREE.Object3D }) => {
      if (disposed) return;
      const model = gltf.scene;

      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      model.traverse((obj: THREE.Object3D) => {
        const name = obj.name ?? "";
        if (!name.startsWith(ZONE_PREFIX)) return;
        const zoneId = name.slice(ZONE_PREFIX.length) as ZoneId;

        const zoneMeshes: THREE.Mesh[] = [];
        obj.traverse((c: THREE.Object3D) => {
          if ((c as THREE.Mesh).isMesh) zoneMeshes.push(c as THREE.Mesh);
        });
        if (!zoneMeshes.length) return;

        if (IGNORED_ZONES.has(zoneId)) {
          for (const m of zoneMeshes) {
            m.material = new THREE.MeshStandardMaterial({
              color: 0x6b7480,
              roughness: 0.8,
              metalness: 0,
              transparent: true,
              opacity: 0.18,
            });
            m.visible = false;
          }
          return;
        }
        for (const m of zoneMeshes) {
          m.material = new THREE.MeshStandardMaterial({
            color: COLOR_BY_INTENSITY[0],
            roughness: 0.55,
            metalness: 0.05,
          });
          (m.userData as { zoneId?: ZoneId }).zoneId = zoneId;
        }
        stateRef.current.meshes.set(zoneId, zoneMeshes);
      });

      scene.add(model);
      cb.setMeshCount(stateRef.current.meshes.size);
    },
    undefined,
    (err: unknown) => {
      if (disposed) return;
      cb.setLoadError(err instanceof Error ? err.message : "Errore caricamento");
    }
  );

  // ── Raycaster events ─────────────────────────────────────
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const getMouse = (e: PointerEvent): THREE.Vector2 => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    return mouse;
  };

  const pickZone = (e: PointerEvent): ZoneId | null => {
    getMouse(e);
    raycaster.setFromCamera(mouse, camera);
    const targets: THREE.Mesh[] = [];
    for (const arr of stateRef.current.meshes.values()) targets.push(...arr);
    const hits = raycaster.intersectObjects(targets, false);
    if (!hits.length) return null;
    return (hits[0].object.userData as { zoneId?: ZoneId }).zoneId ?? null;
  };

  const onPointerMove = (e: PointerEvent) => {
    const zone = pickZone(e);
    if (zone !== stateRef.current.hoveredZone) {
      stateRef.current.hoveredZone = zone;
      cb.setHoveredZone(zone);
      renderer.domElement.style.cursor = zone ? "pointer" : "grab";
    }
  };

  let pointerDownAt = { x: 0, y: 0, t: 0 };
  const onPointerDown = (e: PointerEvent) => {
    pointerDownAt = { x: e.clientX, y: e.clientY, t: performance.now() };
  };
  const onPointerUp = (e: PointerEvent) => {
    const dx = e.clientX - pointerDownAt.x;
    const dy = e.clientY - pointerDownAt.y;
    const dt = performance.now() - pointerDownAt.t;
    if (Math.abs(dx) + Math.abs(dy) < 5 && dt < 300) {
      const zone = pickZone(e);
      if (zone) {
        stateRef.current.selectedZone = zone;
        cb.setSelectedZone(zone);
        stateRef.current.onSelectZone(zone);
        zoomToZoneImpl(stateRef.current, zone);
      } else {
        stateRef.current.selectedZone = null;
        cb.setSelectedZone(null);
        stateRef.current.onSelectZone(null);
      }
    }
  };

  renderer.domElement.style.cursor = "grab";
  renderer.domElement.addEventListener("pointermove", onPointerMove);
  renderer.domElement.addEventListener("pointerdown", onPointerDown);
  renderer.domElement.addEventListener("pointerup", onPointerUp);

  // ── Animate loop ─────────────────────────────────────────
  let rafId = 0;
  const animate = () => {
    rafId = requestAnimationFrame(animate);
    const s = stateRef.current;
    camera.position.lerp(s.targetPos, 0.12);
    controls.target.lerp(s.targetLook, 0.12);
    controls.update();
    renderer.render(scene, camera);
  };
  animate();

  // ── Resize ──────────────────────────────────────────────
  const handleResize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };
  window.addEventListener("resize", handleResize);

  return () => {
    disposed = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener("resize", handleResize);
    renderer.domElement.removeEventListener("pointermove", onPointerMove);
    renderer.domElement.removeEventListener("pointerdown", onPointerDown);
    renderer.domElement.removeEventListener("pointerup", onPointerUp);
    controls.dispose();
    dracoLoader.dispose();
    pmrem.dispose();
    scene.traverse((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry?.dispose();
        const m = mesh.material;
        if (Array.isArray(m)) m.forEach((mat) => mat.dispose());
        else if (m) (m as THREE.Material).dispose();
      }
    });
    renderer.dispose();
    if (renderer.domElement.parentNode) {
      renderer.domElement.parentNode.removeChild(renderer.domElement);
    }
  };
}

function applyColor(
  mesh: THREE.Mesh,
  zoneId: ZoneId,
  intensity: Intensity,
  hovered: ZoneId | null,
  selected: ZoneId | null
) {
  const mat = mesh.material as THREE.MeshStandardMaterial;
  if (!mat?.color) return;
  let color: string = COLOR_BY_INTENSITY[intensity];
  let emissive = "#000000";
  if (selected === zoneId) {
    emissive = COLOR_SELECTED;
    mat.emissiveIntensity = 0.15;
  } else if (hovered === zoneId) {
    emissive = COLOR_HOVER;
    mat.emissiveIntensity = 0.3;
  } else {
    mat.emissiveIntensity = 0;
  }
  mat.color.set(color);
  mat.emissive.set(emissive);
}

function zoomToZoneImpl(state: ViewerState, zoneId: ZoneId) {
  const meshes = state.meshes.get(zoneId);
  if (!meshes?.length || !state.camera) return;
  const box = new THREE.Box3();
  for (const m of meshes) box.expandByObject(m);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const distance = Math.max(maxDim * 2.2, 0.6);

  state.targetLook.copy(center);
  state.targetPos.set(
    center.x + distance * 0.35,
    center.y + distance * 0.15,
    center.z + distance,
  );
}

function resetCameraImpl(state: ViewerState) {
  state.targetPos.copy(DEFAULT_CAM_POS);
  state.targetLook.copy(DEFAULT_CAM_TARGET);
  state.selectedZone = null;
  state.onSelectZone(null);
}
